/**
 * The backoffice's English catalog. Its strings live here rather than in
 * @momo/i18n because that package is Revent's, and Revent never names Revia;
 * the mechanism is the same — schmancy's createLocalization over a { de, en }
 * pair (localize.ts), German first.
 */
export const en = {
	backoffice: {
		setup: {
			draftSavedTitle: "Draft saved",
			draftLocation:
				"The draft stays on this device in this browser, linked to your signed-in account.",
			draftResume:
				"Reopen Add client to continue here. The draft is not available on another device or in another browser.",
			draftEffect:
				"No licence has been issued and nothing has been configured in Google Cloud or GitHub.",

			firebasePurpose:
				"This selects the existing project for the app. No file is needed.",
			providerHint: "Full resource name from Google Cloud",
			officialGuide: "Read Google’s guide",
			providerError:
				"Paste the full provider resource name shown in the instructions.",
			accountError:
				"Paste the service account email ending in .iam.gserviceaccount.com.",
			hostnameError: "Enter a hostname without https:// or a path.",

			heading: "Set up client",
			close: "Close",
			clientStep: "Client",
			clientDesc: "Name and licence term",
			projectStep: "Firebase project",
			projectDesc: "Copy the project ID from Firebase Console",
			accessStep: "Deployment access",
			accessDesc: "Copy two values from Google Cloud",
			reviewStep: "Review and connect",
			reviewDesc: "The licence is issued only at this step",
			next: "Continue",
			saveDraft: "Save as draft",
			openFirebase: "Open Firebase Console",
			findProvider: "Find provider — open instructions",
			findAccount: "Find service account — open instructions",
			providerLabel: "Workload Identity provider",
			accountLabel: "Deployment service account",
			accessPrerequisite:
				"The client needs an existing Workload Identity provider and an authorised service account. These values connect that setup; they do not create cloud resources.",
			setupGuide: "Not set up yet? Open Google’s setup guide",
			consequence:
				"This signs the licence and configures the GitHub environment. It does not deploy the app.",
			advanced: "Other setup methods",
			openConsole: "Open console",
			understood: "Got it",
			firebase1: "Open Firebase Console and select the client’s project.",
			firebase2:
				"Next to Project Overview, click the gear. Open Project settings → General.",
			firebase3:
				"Under Your project, copy Project ID and paste it here. Use the ID, not the project name or project number.",
			providerTitle: "Find the Workload Identity provider",
			provider1:
				"Open Google Cloud Console and select the project containing the configured provider.",
			provider2:
				"Open IAM & Admin → Workload Identity Federation. Select the pool configured for GitHub, then its provider.",
			provider3:
				"Copy the full resource name: projects/PROJECT-NUMBER/locations/global/workloadIdentityPools/POOL-ID/providers/PROVIDER-ID. Paste it here, not the display name.",
			providerFile:
				"If there is no suitable pool or provider, complete the linked Google guide for GitHub first.",
			accountTitle: "Find the service account email",
			account1:
				"Open Google Cloud Console and select the project containing the service account.",
			account2:
				"Open IAM & Admin → Service Accounts. Select the account already authorised for deployment.",
			account3:
				"Copy its email address and paste it here, for example deploy@client-project.iam.gserviceaccount.com.",
			accountFile: "No key is needed. Do not create or upload a JSON key file.",
		},
		brand: "Revia",
		retry: "Try again",
		cancel: "Cancel",
		denied: "This account can't do this.",
		final: "That didn't go through, and trying again won't change it.",
		nav: {
			requests: "Requests",
			licences: "Licences",
			visits: "Visits",
			preferences: "Preferences",
			brandPreferences: "{{brand}} — preferences",
		},
		session: {
			checking: "Checking who is signed in",
		},
		notStaff: {
			title: "This account isn't Revia staff",
			signedInAs: "You're signed in as {{email}}.",
			switch: "Use another account",
		},
		notice: {
			loading: "Checking the staff notice",
			failed: "The staff notice couldn't be checked",
			signingInAs: "Signing in as",
			continue: "Continue",
			recordFailed: "Your acknowledgment wasn't recorded. Try again.",
		},
		requests: {
			waitingLabel: "requests waiting",
			loading: "Loading access requests",
			failed: "Requests didn't load",
			show: "Show requests",
			waiting: "Waiting {{count}}",
			later: "Later {{count}}",
			grantedChip: "Granted {{count}}",
			noneYet: "No requests yet. They arrive here from the form on revia.to.",
			noneWaiting: "Nothing waiting.",
			noneLater: "Nothing set aside for later.",
			noneGranted: "Nobody granted yet.",
			kind: "Kind",
			phone: "Phone",
			email: "Email",
			mailLanguage: "Mail language",
			requested: "Requested",
			kinds: {
				theatre: "Theatre",
				concerts: "Concerts",
				club: "Club",
				festival: "Festival",
				comedy: "Comedy",
				sports: "Sports",
				other: "Other",
			},
			languages: { de: "German", en: "English" },
			on: "on {{date}}",
			expires: "Expires {{when}}.",
			setAside: "Set aside for later {{when}}. Expires {{expires}}.",
			grantedOn: "Granted {{when}}.",
			grantedMailed: "Granted {{when}}. The sign-in link went to {{email}}.",
			setLater: "Later",
			setLaterFor: "Set {{organization}} aside for later",
			backToWaiting: "Back to Waiting",
			backToWaitingFor: "Return {{organization}} to Waiting",
			approve: "Approve",
			approveFor: "Approve {{organization}}",
			foundAsFor: "Name to found {{organization}} under",
			foundAsHint: "Approving founds the organization under this name.",
			foundAsInvalid: "Enter a name of up to 200 characters.",
			retry: "Didn't go through. Try again.",
			denied: "This account can't act on requests.",
			missing: "This request no longer exists.",
			settled: "This request is already granted.",
		},
		licences: {
			loading: "Loading licences",
			failed: "Licences didn't load",
			countOne: "licence",
			countOther: "licences",
			add: "Add client",
			emptyTitle: "No licences yet",
			emptyBody: "Add one once Revent runs in a client's Google Cloud.",
			client: "Client",
			project: "Firebase project",
			signed: "Signed",
			licence: "Licence",
			ends: "Ends {{date}}",
			grace: "Grace until {{date}}",
			readOnlySince: "Read-only since {{date}}",
			sign: "Re-sign",
			signFor: "Re-sign the licence for {{client}}",
			token: "Token",
			tokenFor: "Licence token for {{client}}",
		},
		visits: {
			heading: "Visits",
			span: "{{from}} – {{to}} (UTC)",
			show: "Show",
			days7: "7 days",
			days30: "30 days",
			loading: "Loading the visit report",
			failed: "The visit report didn't load",
			pageViews: "Page views",
			views: "{{count}} views",
			empty: "No page views in this period.",
			referrers: "Referrer",
			unreferred: "No referrer",
			sources: "Campaign source",
			other: "Other",
			noSources: "No campaign in this period.",
		},
		sign: {
			heading: "Sign licence",
			projectId: "Firebase project id",
			idChars: "Use only lowercase letters, digits and hyphens.",
			idStart: "Start with a letter.",
			idLength: "Use 6 to 30 characters; this has {{count}}.",
			idEnd: "End with a letter or digit.",
			endDate: "End date",
			graceDays: "Grace days",
			days: "days",
			summary:
				"{{client}} is licensed until {{end}}, then read-only from {{readOnly}} (UTC).",
			summaryNone: "{{client}} is licensed until the end date you pick.",
			replacesEnding: "Replaces the licence ending {{date}}.",
			replacesEnded: "Replaces the licence that ended {{date}}.",
			pickAfterToday: "Pick a day after today.",
			pickEnd: "Pick an end date.",
			passed: "That day has passed. Pick a later one.",
			deliversAutomatically:
				"Revia's GitHub App then sets LICENCE_TOKEN on {{environment}}.",
			deliversByHand: "Then set the token as LICENCE_TOKEN on {{environment}}.",
			notSetUp:
				"Signing isn't set up for Revia yet, so nothing was signed. Nothing on your side caused this.",
			submit: "Sign licence",
			retry: "Failed · Sign again",
		},
		client: {
			heading: "Add client",
			client: "Client",
			environment: "GitHub environment: {{name}}",
			environmentEmpty: "Its GitHub environment is named from this.",
			environmentNone:
				"The name needs a letter or digit for its GitHub environment.",
			serviceAccountHint: "The client's deploy service account address",
			mainHostname: "Custom domain (optional)",
			mainHostnameHint: "Only once it is attached to the client's Hosting site",
			pathway: "How to set up GitHub",
			automatic: "Automatically",
			commands: "Commands",
			github: "On github.com",
			checking: "Checking GitHub…",
			checkFailed: "Couldn't check GitHub.",
			connected:
				"Revia's GitHub App sets up {{environment}} in ReVia-Workspace/Revent.",
			notConnected:
				"GitHub isn't connected. Connect GitHub registers Revia's App on ReVia-Workspace; an owner of the organization finishes it on github.com, then installs it on Revent.",
			connectGitHub: "Connect GitHub",
			notInstalled:
				"Revia's GitHub App is registered but not installed on Revent.",
			install: "Install the App",
			recheck: "Check again",
			connect: "Connect automatically",
			connectRetry: "Failed · Connect again",
			signFirst:
				"The licence is signed first; its token then goes into the environment.",
			commandsLine:
				"Run these in a terminal signed in to GitHub (gh auth login). The secret command asks for its value: paste the token at the hidden prompt.",
			commandsSubject: "commands",
			copyCommands: "Copy the commands",
			openEnvironments: "Open Revent's environments",
			environmentSubject: "environment name",
			step1: "New environment, named:",
			step2:
				"Deployment branches and tags → Selected branches and tags → Add rule → Tag, with the pattern:",
			step3: "Environment variables:",
			step4: "Environment secret LICENCE_TOKEN:",
			step5:
				"Settings → Secrets and variables → Actions → Variables: add the name to the JSON array in DEPLOY_ENVIRONMENTS:",
			openVariables: "Open the repository's variables",
		},
		steps: {
			licence: "Licence",
			environment: "Environment",
			branchPolicy: "Deploys from v* tags only",
			variables: "Variables",
			secret: "LICENCE_TOKEN secret",
			roster: "Listed in DEPLOY_ENVIRONMENTS",
			done: "done",
			failed: "failed",
			skipped: "not run",
			credentials:
				"GitHub rejected the App's credentials. Register it again with Connect GitHub.",
			forbidden:
				"GitHub refused it. On GitHub Free, a private repository's environments and their secrets need GitHub Team; otherwise the App lacks this permission.",
			notFound:
				"GitHub found no such repository, or the App isn't installed on it.",
			rejected:
				"GitHub declined it. On GitHub Free, a private repository's environments need GitHub Team.",
			rosterInvalid:
				"DEPLOY_ENVIRONMENTS doesn't hold a JSON array of names. Correct it on github.com first.",
			unexpected: "GitHub answered HTTP {{status}}.",
			manual:
				"The licence is signed. Finish the rest by hand: choose Commands or On github.com.",
		},
		token: {
			heading: "Licence token for {{client}}",
			line: "Set it as the secret LICENCE_TOKEN on the client's environment in ReVia-Workspace/Revent. Run this with the client's environment name, then paste the token at the hidden prompt:",
			command: "Command",
			value: "Token",
			subject: "licence token",
			done: "Done",
			onGitHub: "On github.com",
		},
	},
} as const;

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
/** Every locale carries exactly English's keys. */
export type Catalog = Shape<typeof en>;
